import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import BeatLoader from "react-spinners/BeatLoader";
import { NacApiError, fetchPublicNac, respondPublicNac } from "../services/nacService";
import { detailToForm } from "../utils/nacValidation";
import NacReadOnly from "./NacReadOnly";

const MESSAGES: Record<string, string> = {
  USED: "Questo link è già stato utilizzato. Non occorre fare altro.",
  EXPIRED: "Questo link è scaduto. Chieda al suo agente di inviarne uno nuovo.",
  VOID: "I dati sono stati modificati o il link è stato sostituito. Usi il link più recente ricevuto per e-mail, oppure chieda al suo agente di inviarne uno nuovo.",
  SUBMITTED: "Questa richiesta è già stata inviata ai nostri uffici. Non occorre fare altro.",
};

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-neutral-100 px-4 py-8">
      <div className="mx-auto max-w-5xl">
        <h1 className="mb-1 text-2xl font-bold">CEDI – Conferma dati nuova anagrafica cliente</h1>
        {children}
      </div>
    </div>
  );
}

export default function NacConfirm() {
  const { token = "" } = useParams();
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [consenso, setConsenso] = useState<"" | "si" | "no">("");
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const [done, setDone] = useState<"" | "accepted" | "rejected">("");

  useEffect(() => {
    (async () => {
      try {
        setData(await fetchPublicNac(token));
      } catch (e) {
        setError(e instanceof NacApiError ? e.message : "Servizio non disponibile, riprovare più tardi");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const send = async (body: Parameters<typeof respondPublicNac>[1], outcome: "accepted" | "rejected") => {
    setBusy(true);
    setError("");
    try {
      await respondPublicNac(token, body);
      setDone(outcome);
    } catch (e) {
      setError(e instanceof NacApiError ? e.message : "Operazione non riuscita, riprovare più tardi");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <div className="py-20 text-center">
          <BeatLoader color="#1d4ed8" />
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="mt-6 rounded-lg border border-green-300 bg-green-50 p-5">
          {done === "accepted" ? (
            <p>Grazie, i dati sono stati confermati. Il suo agente può ora completare la richiesta: non occorre fare altro.</p>
          ) : (
            <p>Grazie, abbiamo comunicato al suo agente le modifiche richieste. Riceverà un nuovo link per controllare i dati aggiornati.</p>
          )}
        </div>
      </Shell>
    );
  }

  if (!data) {
    return (
      <Shell>
        <div className="mt-6 rounded-lg border border-red-300 bg-red-50 p-5" role="alert">
          {error || "Link non valido."}
        </div>
      </Shell>
    );
  }

  if (data.link_state !== "PENDING") {
    return (
      <Shell>
        <div className="mt-6 rounded-lg border border-yellow-300 bg-yellow-50 p-5">{MESSAGES[data.link_state] ?? "Link non più valido."}</div>
      </Shell>
    );
  }

  const values = detailToForm(data);
  return (
    <Shell>
      <p className="mb-4 text-gray-700">
        Il suo agente <b>{data.request.agent_name}</b> ha inserito i dati seguenti per aprire la sua anagrafica cliente. Li controlli con attenzione: può confermarli oppure richiedere modifiche.
      </p>

      <NacReadOnly values={values} lookups={data.lookups} hideConfirmation hideCommercial />

      <section className="mb-10 rounded-lg border border-neutral-200 bg-white p-5">
        <h2 className="mb-3 text-xl font-bold">La sua risposta</h2>
        {error && (
          <div className="mb-3 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800" role="alert">
            {error}
          </div>
        )}
        {!rejecting ? (
          <div className="space-y-4">
            <fieldset>
              <legend className="mb-1 font-medium">
                Consenso all'invio telematico dei documenti <span className="text-red-600">*</span>
              </legend>
              <label className="mr-6">
                <input type="radio" name="consenso" checked={consenso === "si"} onChange={() => setConsenso("si")} /> Sì
              </label>
              <label>
                <input type="radio" name="consenso" checked={consenso === "no"} onChange={() => setConsenso("no")} /> No
              </label>
            </fieldset>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                disabled={busy || !consenso}
                onClick={() => send({ outcome: "accept", consenso_invio_telematico: consenso as "si" | "no" }, "accepted")}
                className="bg-blue-600 px-5 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Accetto i dati
              </button>
              <button type="button" disabled={busy} onClick={() => setRejecting(true)} className="border border-blue-600 bg-white px-5 py-2 font-semibold text-blue-600 hover:bg-blue-50 disabled:opacity-50">
                Richiedi modifiche
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="block font-medium" htmlFor="nac-note">
              Cosa deve essere corretto? <span className="text-red-600">*</span>
            </label>
            <textarea id="nac-note" className="w-full rounded border border-neutral-300 p-2" rows={4} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                disabled={busy || note.trim().length < 3}
                onClick={() => send({ outcome: "reject", note: note.trim() }, "rejected")}
                className="bg-blue-600 px-5 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Invia richiesta di modifica
              </button>
              <button type="button" disabled={busy} onClick={() => setRejecting(false)} className="border border-neutral-300 bg-white px-5 py-2 hover:bg-neutral-50">
                Indietro
              </button>
            </div>
          </div>
        )}
      </section>
    </Shell>
  );
}
